/* import __COLOCATED_TEMPLATE__ from './reasons-interactive-bar.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type IntlService from 'ember-intl/services/intl';
import { type OptimizeSuggestionItem } from 'embercom/services/optimize-suggestions-service';
import { inject as service } from '@ember/service';
import type OptimizeSuggestionsService from 'embercom/services/optimize-suggestions-service';
import type { OptimizeSuggestionFilterState } from '../lib/optimize-suggestion-filter-helper';

export interface ReasonData {
  share: number;
  sentToTeammate: number;
  resolved: number;
  unresolved: number;
}

type ReasonKey = 'content-gap' | 'data-gap' | 'action-gap';

interface Signature {
  Args: {
    isEmptyState: boolean;
    suggestions: OptimizeSuggestionItem[];
  };
}

type Segment = {
  key: ReasonKey;
  label: string;
  colorClass: string;
  share: number;
};

export default class ReasonsInteractiveBar extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare optimizeSuggestionsService: OptimizeSuggestionsService;

  get contentSuggestions(): OptimizeSuggestionItem[] {
    return this.args.suggestions.filter((s) => s.suggestionType === 'content-gap');
  }

  get dataSuggestions(): OptimizeSuggestionItem[] {
    return this.args.suggestions.filter((s) => s.suggestionType === 'data-gap');
  }

  get actionSuggestions(): OptimizeSuggestionItem[] {
    return this.args.suggestions.filter((s) => s.suggestionType === 'action-gap');
  }

  get selectedReasonType(): ReasonKey | null {
    if (this.optimizeSuggestionsService.filterState?.suggestionType.length === 0) {
      return null;
    }
    if (this.optimizeSuggestionsService.filterState?.suggestionType.length === 1) {
      return this.optimizeSuggestionsService.filterState?.suggestionType[0] as ReasonKey;
    }
    return null;
  }

  get segments(): Segment[] {
    let total = this.args.suggestions.length;
    return [
      {
        key: 'content-gap',
        label: 'CONTENT GAPS',
        colorClass: 'bg-vis-mantis-10',
        share: total > 0 ? Math.round((this.contentSuggestions.length / total) * 100) : 0,
      },
      {
        key: 'data-gap',
        label: 'DATA GAPS',
        colorClass: 'bg-vis-mantis-40',
        share: total > 0 ? Math.round((this.dataSuggestions.length / total) * 100) : 0,
      },
      {
        key: 'action-gap',
        label: 'ACTION GAPS',
        colorClass: 'bg-vis-mantis-60',
        share: total > 0 ? Math.round((this.actionSuggestions.length / total) * 100) : 0,
      },
    ];
  }

  get gridTemplateColumns() {
    let spans = this.segments.map((segment) => (segment.share > 0 ? segment.share : 1));
    return spans.map((span) => `${span}fr`).join(' ');
  }

  get hasSelection() {
    return this.selectedReasonType !== null;
  }

  @action
  filterBy(reasonKey: ReasonKey) {
    let currentFilter = this.optimizeSuggestionsService.filterState?.suggestionType ?? [];
    // Toggle: if this type is already filtered, clear the filter
    let isAlreadyFiltered = currentFilter.length === 1 && currentFilter[0] === reasonKey;
    this.optimizeSuggestionsService.setFilterState({
      ...(this.optimizeSuggestionsService.filterState as OptimizeSuggestionFilterState),
      suggestionType: isAlreadyFiltered ? [] : [reasonKey],
    });
  }

  @action
  handleSelect(key: ReasonKey) {
    this.filterBy(key);
  }

  @action
  getReasonData(key: ReasonKey): ReasonData {
    let suggestionsByKey: Record<ReasonKey, OptimizeSuggestionItem[]> = {
      'content-gap': this.contentSuggestions,
      'data-gap': this.dataSuggestions,
      'action-gap': this.actionSuggestions,
    };

    let suggestions = suggestionsByKey[key];
    let total = this.args.suggestions.length;
    let share = total > 0 ? (suggestions.length / total) * 100 : 0;

    // TODO: Calculate actual values from suggestion data
    return {
      share,
      sentToTeammate: 0,
      resolved: 0,
      unresolved: 0,
    };
  }

  @action
  getReasonTitle(key: ReasonKey): string {
    let titles: Record<ReasonKey, string> = {
      'content-gap': 'CONTENT GAPS',
      'data-gap': 'DATA GAPS',
      'action-gap': 'ACTION GAPS',
    };
    return titles[key];
  }

  @action
  getReasonDescription(key: ReasonKey): string {
    let descriptions: Record<ReasonKey, string> = {
      'content-gap': this.intl.t(
        'automation.analyze.task-suggestions.suggestions.reason-bar-tooltip.descriptions.content-gaps',
      ),
      'data-gap': this.intl.t(
        'automation.analyze.task-suggestions.suggestions.reason-bar-tooltip.descriptions.data-gaps',
      ),
      'action-gap': this.intl.t(
        'automation.analyze.task-suggestions.suggestions.reason-bar-tooltip.descriptions.action-gaps',
      ),
    };
    return descriptions[key];
  }

  @action
  getReasonIcon(key: ReasonKey): InterfaceIconName {
    let icons: Record<ReasonKey, InterfaceIconName> = {
      'content-gap': 'knowledge',
      'data-gap': 'recipient-data',
      'action-gap': 'webhook',
    };
    return icons[key];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::OptimizeSuggestions::Suggestions::ReasonsInteractiveBar': typeof ReasonsInteractiveBar;
    'reporting/automation/ai-insights/optimize-suggestions/suggestions/reasons-interactive-bar': typeof ReasonsInteractiveBar;
  }
}
